import type { KeyValueStore } from '@/utils/sessionHandoff'

/** Prefijo con el que Amplify v5 nombra las llaves de sesion en localStorage. */
export const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider.'

export interface CognitoPool {
  region: string
  userPoolId: string
  clientId: string
}

/** The region a user pool lives in: the prefix of its id (`eu-west-1_abc` -> `eu-west-1`). */
export function poolRegion(userPoolId: string): string | null {
  const match = /^([a-z]{2}(?:-[a-z]+)+-\d+)_\w+$/.exec(userPoolId)
  return match ? match[1] : null
}

/** This build's own pool, the one its login, invite and reset pages sign in against. */
export function homeCognitoPool(): CognitoPool {
  return {
    region: import.meta.env.VITE_AWS_REGION,
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL,
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
  }
}

/** The pool whose staff sessions this build accepts through the magic login, or null when the build has none. */
export function staffCognitoPool(): CognitoPool | null {
  const userPoolId = import.meta.env.VITE_STAFF_COGNITO_USER_POOL?.trim()
  const clientId = import.meta.env.VITE_STAFF_COGNITO_CLIENT_ID?.trim()
  if (!userPoolId || !clientId) return null
  const region = poolRegion(userPoolId)
  return region === null ? null : { region, userPoolId, clientId }
}

/** Why the staff vars cannot be used, or null when they are valid or both unset. */
export function staffPoolConfigError(): string | null {
  const userPoolId = import.meta.env.VITE_STAFF_COGNITO_USER_POOL?.trim()
  const clientId = import.meta.env.VITE_STAFF_COGNITO_CLIENT_ID?.trim()
  if (!userPoolId && !clientId) return null
  if (!userPoolId || !clientId) {
    return 'VITE_STAFF_COGNITO_USER_POOL and VITE_STAFF_COGNITO_CLIENT_ID must be set together; staff pool mode is off'
  }
  if (poolRegion(userPoolId) === null) {
    return `VITE_STAFF_COGNITO_USER_POOL "${userPoolId}" is not a user pool id; staff pool mode is off`
  }
  return null
}

/** Cognito clients whose keys a redeemed handoff may write on this origin. */
export function acceptedClientIds(): string[] {
  const staff = staffCognitoPool()
  return staff === null ? [homeCognitoPool().clientId] : [homeCognitoPool().clientId, staff.clientId]
}

export function hasClientSession(store: KeyValueStore, clientId: string): boolean {
  return store.getItem(`${COGNITO_KEY_PREFIX}${clientId}.LastAuthUser`) !== null
}

const STAFF_GROUP = 'Finnecto'

function idTokenGroups(idToken: string | null): unknown {
  try {
    const payload = idToken?.split('.')[1]
    if (!payload) return null
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))['cognito:groups']
  } catch {
    return null
  }
}

/**
 * A stored staff-client session that belongs to Finnecto staff. The staff client is also the US tenant client,
 * so an old US login on this host leaves the same keys; only the group tells them apart.
 */
function hasStaffSession(store: KeyValueStore, staff: CognitoPool): boolean {
  const prefix = `${COGNITO_KEY_PREFIX}${staff.clientId}.`
  const user = store.getItem(`${prefix}LastAuthUser`)
  if (user === null) return false
  const groups = idTokenGroups(store.getItem(`${prefix}${user}.idToken`))
  return Array.isArray(groups) && groups.includes(STAFF_GROUP)
}

/** The staff pool while the origin holds a staff session and no home session, otherwise the home pool. */
export function resolveCognitoPool(store: KeyValueStore): CognitoPool {
  const staff = staffCognitoPool()
  if (staff === null) return homeCognitoPool()
  const home = homeCognitoPool()
  if (!hasClientSession(store, home.clientId) && hasStaffSession(store, staff)) return staff
  return home
}

/** resolveCognitoPool, dropping (without revoking) staff-client keys it does not pick. */
export function settleCognitoPool(store: KeyValueStore): CognitoPool {
  const pool = resolveCognitoPool(store)
  const staff = staffCognitoPool()
  if (staff !== null && !samePool(pool, staff)) dropClientKeys(store, staff.clientId)
  return pool
}

export function samePool(a: CognitoPool, b: CognitoPool): boolean {
  return a.region === b.region && a.userPoolId === b.userPoolId && a.clientId === b.clientId
}

/** Removes every `CognitoIdentityServiceProvider.<clientId>.*` key. */
export function dropClientKeys(store: KeyValueStore, clientId: string): void {
  const prefix = `${COGNITO_KEY_PREFIX}${clientId}.`
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.removeItem(key)
  }
}

/** This origin's localStorage, where Amplify keeps every client's session. */
export function localStorageStore(): KeyValueStore {
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
    clear: () => window.localStorage.clear(),
    keys: () => Object.keys(window.localStorage),
  }
}

let activePool: CognitoPool | null = null

/** The pool Amplify is configured with; the home pool until something configures another. */
export function activeCognitoPool(): CognitoPool {
  return activePool ?? homeCognitoPool()
}

export function setActiveCognitoPool(pool: CognitoPool): void {
  activePool = pool
}
